import { useMemo } from "react"
import { CustomField, customFieldLabel } from "@/schema"
import { DataTableColumn } from "@ui/data-table/columns"

export function buildCustomFieldColumn<TData = unknown>(
  field: CustomField,
  enableEditing = true
): DataTableColumn<TData> {
  const meta = { customField: field }
  const name = customFieldLabel(field)

  switch (field.field_type) {
    case "cross_reference":
      return {
        accessorKey: field.field_key,
        name,
        sortBy: field.field_key,
        enableEditing,
        meta,
        inputCellComponent: "CrossReferenceColumnInput",
        displayCellComponent: "CrossReferenceColumnDisplay",
      }
    case "rich_text":
      return {
        accessorKey: field.field_key,
        name,
        sortBy: field.field_key,
        enableEditing,
        meta,
        inputCellComponent: "RichTextColumnInput",
        displayCellComponent: "RichTextColumnDisplay",
      }
    default:
      return {
        accessorKey: field.field_key,
        name,
        sortBy: field.field_key,
        enableEditing,
        meta,
        inputCellComponent: "CustomFieldInput",
        displayCellComponent: "CustomFieldDisplay",
      }
  }
}

export function useColumnsWithCustomFields<TData>(
  baseColumns: DataTableColumn<TData>[],
  customFields?: CustomField[],
  enableEditing = true
): DataTableColumn[] {
  return useMemo(() => {
    // Legacy custom fields can share a field_key with a built-in column (e.g. "state").
    // Two column defs with the same accessor id shift every cell in TanStack, so the
    // built-in column always wins and the colliding custom column is dropped.
    const builtInAccessorKeys = new Set(baseColumns.map((column) => column.accessorKey as string))

    const customFieldColumns = (customFields ?? [])
      .filter((field) => !builtInAccessorKeys.has(field.field_key))
      .map((field) => buildCustomFieldColumn<TData>(field, enableEditing)) as DataTableColumn<TData>[]

    return [...baseColumns, ...customFieldColumns] as DataTableColumn[]
  }, [baseColumns, customFields, enableEditing])
}
